import React, { useState } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, invoiceStatus, clientPayable, resolveInvoiceFinancials } from '../../core/helpers';
import { Badge } from '../../components/Badge';
import { Icon, InlineIcon } from '../../components/Icon';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { FilterBar } from '../../components/FilterBar';
import { buildOrderParam, useTableTools } from '../../core/tableView';
import { clientPortalUsesApiPaging } from '../../mobile/clientPortal';
import { SortableTh } from '../../components/SortableTh';

const CLIENT_INV_ORDER = { invoiceNo: 'invoice_no', date: 'date', dueDate: 'due_date', total: 'total', paid: 'paid', pending: 'pending' };
const CLIENT_INV_ACCESSORS = {
  invoiceNo: (r) => (r.invoiceNo || '').toLowerCase(),
  date: (r) => (r.date ? new Date(r.date).getTime() : 0),
  dueDate: (r) => (r.dueDate ? new Date(r.dueDate).getTime() : 0),
  total: (r) => Number(r.total) || 0,
  paid: (r) => Number(r.paid) || 0,
  pending: (r) => Number(r.pending) || 0,
  status: (r) => invoiceStatus(r),
};


function ClientInvoices() {
  const { invoices, visibleCompanyIds, openModal, payments, paymentSchedule } = useApp();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const localMyInvoices = invoices.filter(i =>
    visibleCompanyIds.includes(i.clientId)
  ).sort((a,b) => new Date(b.date||0) - new Date(a.date||0));
  const apiFilter = buildApiFilter({
    equals: visibleCompanyIds.length === 1 ? { company_id: visibleCompanyIds[0] } : {},
    search,
    searchFields: ["invoice_no", "client_name"],
    order: buildOrderParam(sortKey, sortDir, CLIENT_INV_ORDER) || "date.desc",
  });
  const apiPaging = clientPortalUsesApiPaging(visibleCompanyIds.length === 1);
  const { rows: myInvoicesRaw, hasMore, loadMore, loadingMore } = usePaginatedApiRows("invoices", apiFilter, localMyInvoices, { enabled: apiPaging });
  const myInvoices = myInvoicesRaw
    .map(inv => resolveInvoiceFinancials(inv, payments))
    .filter(inv => statusFilter === "all" || invoiceStatus(inv) === statusFilter || (statusFilter === "unpaid" && invoiceStatus(inv) === "overdue"));
  const table = useTableTools(myInvoices, {
    accessors: CLIENT_INV_ACCESSORS,
    sortKey,
    sortDir,
    onSort: toggleSort,
    apiPaged: apiPaging,
  });

  const instalmentPlanTotal = (inv) => {
    const rows = paymentSchedule.filter(s => s.invoiceId === inv.id);
    if (!rows.length) return inv.total || 0;
    return rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  };

  const liveInvoices = myInvoices.filter(i => i.status !== "void");
  const totalBilled    = liveInvoices.reduce((s,i)=>s+instalmentPlanTotal(i),0);
  const totalPaid      = liveInvoices.reduce((s,i)=>s+i.paid,0);
  const totalPending   = liveInvoices.reduce((s,i)=>{
    const payable = clientPayable(i, paymentSchedule);
    return s + (payable.hasInstallments ? payable.amount : (i.pending||0));
  },0);

  return (
    <>
      <div className="stat-grid grid4" style={{marginBottom:20}}>
        {[
          ["Total Billed",   INR(totalBilled),  "var(--navy)"],
          ["Paid",           INR(totalPaid),    "var(--green)"],
          ["Outstanding",    INR(totalPending), totalPending>0?"var(--red)":"var(--muted)"],
          ["Invoices",       myInvoices.length, "var(--blue)"],
        ].map(([l,v,c])=>(
          <div key={l} className="stat-box" style={{cursor:"default"}}>
            <div className="stat-lbl">{l}</div>
            <div className="stat-val" style={{fontSize:20,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      <FilterBar
        search={search} setSearch={setSearch} searchPlaceholder="Search invoice #…"
        onClear={() => { setSearch(""); setStatusFilter("all"); }}
        extra={
          <select className="f-select list-toolbar-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="unpaid">Unpaid</option>
          </select>
        }
      />

      <div className="card">
        <div className="card-head"><div className="card-title">My Invoices</div></div>
        {myInvoices.length === 0 ? (
          <div className="empty"><div className="empty-icon"><Icon name="doc" size={28} /></div><div>No invoices yet</div></div>
        ) : (
          <div className="tbl-freeze-col table-scroll-shell">
          <div className="tbl-wrap tbl-m-billing">
            <table>
              <thead>
                <tr>
                  <SortableTh label="Invoice No" sortKey="invoiceNo" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Date" sortKey="date" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Due Date" sortKey="dueDate" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Invoice total" sortKey="total" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortableTh label="Paid" sortKey="paid" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th>Due now</th>
                  <SortableTh label="Status" sortKey="status" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {table.visible.map(inv => {
                  const payable = clientPayable(inv, paymentSchedule);
                  return (
                  <tr key={inv.id}>
                    <td>
                      <button
                        type="button"
                        style={{ fontWeight: 700, color: "var(--blue)", fontSize: 12, padding: 0, border: "none", background: "none", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => openModal("view-invoice", { invoice: inv })}
                      >
                        {inv.invoiceNo}
                      </button>
                    </td>
                    <td style={{fontSize:12,color:"var(--muted)"}}>{inv.date}</td>
                    <td style={{fontSize:12,color:payable.amount>0&&inv.dueDate&&new Date(inv.dueDate)<new Date()?"var(--red)":"var(--muted)"}}>{inv.dueDate||"—"}</td>
                    <td style={{fontWeight:600}}>{INR(inv.total)}</td>
                    <td style={{color:"var(--green)",fontWeight:600}}>{INR(inv.paid)}</td>
                    <td style={{color:inv.status==="void"?"var(--muted)":payable.amount>0?"var(--red)":"var(--muted)",fontWeight:600}}>
                      {inv.status === "void" ? "Cancelled" : (payable.hasInstallments ? INR(payable.amount) : INR(inv.pending))}
                    </td>
                    <td><Badge status={invoiceStatus(inv)}/></td>
                    <td>
                      <div style={{display:"flex",gap:6}}>
                        {payable.amount > 0 && (
                          <button className="btn btn-sm btn-primary"
                            onClick={()=>openModal("pay-now",{ invoiceId: inv.id, amount: payable.amount, scheduleId: payable.scheduleId || undefined })}>
                            <InlineIcon name="card" size={13} gap={5}>Pay {INR(payable.amount)}</InlineIcon>
                          </button>
                        )}
                        {inv.status==="paid" && (
                          <span style={{fontSize:11,color:"var(--green)",fontWeight:700}}><InlineIcon name="check" size={11} gap={4}>Fully Paid</InlineIcon></span>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        )}
        <LoadMoreBar
          hasMore={apiPaging ? hasMore : table.hasMore}
          loadingMore={loadingMore}
          onLoadMore={apiPaging ? loadMore : table.loadMore}
          count={table.shownCount}
        />
      </div>
    </>
  );
}

export { ClientInvoices };
